import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Palette, Globe, Sun, Moon, ALargeSmall, Type, Check, Monitor,
  Calendar, Clock, Bell, BookOpen, ChevronDown
} from 'lucide-react';
import { SectionHeader, SubLabel, PremiumBadge } from './SettingsHelpers';
import CollapsibleSettingsCard from '@/components/CollapsibleSettingsCard';
import { subscribeToPush } from '@/services/pushNotifications';
import { requestOneSignalPermission } from '@/utils/oneSignalWeb';
import {
  languages, fontFamilyOptions, fontSizeOptions, glassCard, glassOption,
  days, times, timezones, formatTime
} from './settingsConstants';

export function PreferencesTab({
  colorMode, handleColorModeChange,
  currentTheme, handleThemeChange, themeSwatches, fontFamilies,
  currentFontFamily, handleFontFamilyChange,
  currentFontSize, handleFontSizeChange,
  language, handleLanguageChange,
  updateSetting,
  isPaid, setUpgradeFeature,
  Lock,
  currentDeviceName,
  recapDay, setRecapDay, recapTime, setRecapTime,
  recapTimezone, setRecapTimezone,
  pushRecapEnabled, setPushRecapEnabled,
  pushReflectionEnabled, setPushReflectionEnabled,
  user,
}) {
  const { t } = useTranslation();
  const [notificationsExpanded, setNotificationsExpanded] = React.useState(false);

  return (
    <>
      {/* Appearance */}
      <CollapsibleSettingsCard
        storageKey="appearance"
        tabId="preferences"
        searchTerms="appearance theme color font dark light mode font size typography"
        glassCard={glassCard}
        testId="settings-appearance-section"
        defaultOpen={false}
        icon={<Palette className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
        title={t('settings.appearance')}
        subtitle={t('settings.appearance_subtitle', 'Theme, font, and color mode')}
      >

        {/* Per-device indicator for paid users */}
        {isPaid && (
          <div
            className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
            data-testid="device-preferences-indicator"
          >
            <Monitor className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--theme-accent)' }} />
            <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('settings.device_prefs_label', 'Preferences saved for')} <strong style={{ color: 'var(--theme-text)' }}>{currentDeviceName || t('settings.this_device', 'this device')}</strong>
            </span>
          </div>
        )}

        {/* Light / Dark Toggle */}
        <div
          className="flex rounded-2xl p-1 mb-6"
          style={{ background: 'var(--glass-bg-active)' }}
          data-testid="settings-color-mode-toggle"
        >
          <button
            onClick={() => handleColorModeChange('light')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-all"
            style={{
              background: colorMode === 'light' ? 'var(--theme-accent)' : 'transparent',
              color: colorMode === 'light' ? '#FFFFFF' : 'var(--theme-textSecondary)',
              fontSize: '14px',
            }}
            data-testid="settings-mode-light"
          >
            <Sun className="h-4 w-4" />
            {t('settings.light', 'Light')}
          </button>
          <button
            onClick={() => handleColorModeChange('dark')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-all"
            style={{
              background: colorMode === 'dark' ? 'var(--theme-accent)' : 'transparent',
              color: colorMode === 'dark' ? '#FFFFFF' : 'var(--theme-textSecondary)',
              fontSize: '14px',
            }}
            data-testid="settings-mode-dark"
          >
            <Moon className="h-4 w-4" />
            {t('settings.dark', 'Dark')}
          </button>
        </div>

        {/* Color Theme */}
        <SubLabel icon={Palette}>{t('settings.theme', 'Color Theme')}</SubLabel>
        <div className="grid grid-cols-3 gap-3">
          {themeSwatches.map(({ key, label, darkBg, darkAccent, lightBg, lightAccent }) => {
            const bg = colorMode === 'dark' ? darkBg : lightBg;
            const accent = colorMode === 'dark' ? darkAccent : lightAccent;
            const selected = currentTheme === key;
            const locked = !isPaid && key !== 'light';
            return (
              <button
                key={key}
                onClick={() => locked ? setUpgradeFeature('themes') : handleThemeChange(key)}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all ${locked ? 'opacity-30 grayscale' : ''}`}
                style={glassOption(selected && !locked)}
                data-testid={`settings-theme-${key}`}
              >
                <div
                  className="w-10 h-10 rounded-full relative overflow-hidden"
                  style={{ background: bg, border: '2px solid var(--glass-border)' }}
                >
                  <div
                    className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                    style={{ background: accent }}
                  />
                  {locked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
                      <Lock className="h-3.5 w-3.5 text-white/80" />
                    </div>
                  )}
                </div>
                <span className="font-medium" style={{ color: selected && !locked ? 'var(--theme-text)' : 'var(--theme-textSecondary)', fontSize: '12px' }}>
                  {label}
                </span>
                {selected && !locked && <Check className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />}
              </button>
            );
          })}
        </div>

        {/* Font Family */}
        <SubLabel icon={Type}>{t('settings.font_style', 'Font Style')}</SubLabel>
        <div className="grid grid-cols-2 gap-2">
          {fontFamilyOptions.map((f) => {
            const selected = currentFontFamily === f.key;
            return (
              <button
                key={f.key}
                onClick={() => handleFontFamilyChange(f.key)}
                className="p-3.5 rounded-xl text-left transition-all"
                style={{ ...glassOption(selected), fontFamily: fontFamilies[f.key] }}
                data-testid={`settings-font-${f.key}`}
              >
                <span className="font-medium truncate" style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)', fontSize: '14px' }}>
                  {f.label}
                </span>
                {selected && <Check className="h-3 w-3 ml-1 inline" style={{ color: 'var(--theme-accent)' }} />}
              </button>
            );
          })}
        </div>

        {/* Font Size */}
        <SubLabel icon={ALargeSmall}>{t('settings.font_size', 'Text Size')}</SubLabel>
        <div className="grid grid-cols-4 gap-2" style={{ fontSize: '16px' }}>
          {fontSizeOptions.map((s) => {
            const selected = currentFontSize === s.key;
            return (
              <button
                key={s.key}
                onClick={() => handleFontSizeChange(s.key)}
                className="p-3 rounded-xl text-center transition-all overflow-hidden"
                style={glassOption(selected)}
                data-testid={`settings-size-${s.key}`}
                title={s.fullLabel}
              >
                <span className="font-medium whitespace-nowrap" style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)', fontSize: s.key === 'small' ? '13px' : s.key === 'normal' ? '15px' : s.key === 'large' ? '17px' : '19px' }}>
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
      </CollapsibleSettingsCard>
      {/* Language */}
      <CollapsibleSettingsCard
        storageKey="language"
        tabId="preferences"
        searchTerms="language locale english spanish french portuguese español français português idioma idioma"
        glassCard={glassCard}
        testId="settings-language-section"
        defaultOpen={false}
        icon={<Globe className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
        title={t('settings.language')}
        subtitle={languages.find((l) => l.code === language?.split('-')[0])?.name || ''}
      >
        <div className="grid grid-cols-2 gap-3">
          {languages.map((lang) => {
            const selected = language?.split('-')[0] === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="flex items-center gap-3 p-4 rounded-2xl transition-all"
                style={glassOption(selected)}
                data-testid={`settings-lang-${lang.code}`}
              >
                <span className="text-2xl shrink-0">{lang.flag}</span>
                <span className="font-medium truncate" style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)', fontSize: '14px' }}>
                  {lang.name}
                </span>
                {selected && <Check className="h-4 w-4 ml-auto" style={{ color: 'var(--theme-accent)' }} />}
              </button>
            );
          })}
        </div>
      </CollapsibleSettingsCard>
      {/* Notifications Group (Weekly Recap + Daily Reflection) */}
      {!user?.is_guest && (
        <div data-testid="settings-notifications-group">
          <button
            type="button"
            onClick={() => setNotificationsExpanded((v) => !v)}
            className="rounded-3xl p-6 w-full text-left cursor-pointer"
            style={glassCard}
            data-testid="settings-notifications-toggle"
            aria-expanded={notificationsExpanded}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
                  <Bell className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.notifications', 'Notifications')}</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.notifications_desc', 'Weekly recap & daily reflection alerts')}</p>
                </div>
              </div>
              <ChevronDown
                className="h-5 w-5 transition-transform duration-300 shrink-0"
                style={{ color: 'var(--theme-textSecondary)', transform: notificationsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </div>
          </button>

          <div
            className="overflow-hidden transition-all duration-300"
            style={{
              maxHeight: notificationsExpanded ? '2400px' : '0',
              opacity: notificationsExpanded ? 1 : 0,
              marginTop: notificationsExpanded ? '20px' : '0',
            }}
            aria-hidden={!notificationsExpanded}
          >
            <div className="space-y-5">
              {/* Weekly Recap */}
              <div className={`rounded-3xl p-6 relative ${!isPaid ? 'opacity-40' : ''}`} style={glassCard} data-testid="settings-recap-section">
                {!isPaid && (
                  <div className="absolute inset-0 rounded-3xl z-10 cursor-pointer" onClick={() => setUpgradeFeature('recap')} data-testid="recap-upgrade-overlay" />
                )}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
                    <Calendar className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
                  </div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.weekly_recap', 'Weekly Recap')}</h2>
                  {!isPaid && <PremiumBadge />}
                </div>
                <p className="text-xs mb-5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.recap_description', 'Choose when to receive your weekly study recap notification.')}</p>

                <SubLabel icon={Calendar}>{t('recap_schedule.day_label', 'Day')}</SubLabel>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {days.map((d) => {
                    const selected = recapDay === d.value;
                    return (
                      <button
                        key={d.value}
                        onClick={() => { setRecapDay(d.value); updateSetting('recap_day', d.value); }}
                        className="py-2 rounded-xl text-xs font-medium transition-all text-center"
                        style={glassOption(selected)}
                        data-testid={`settings-recap-day-${d.value}`}
                      >
                        <span style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}>{d.label}</span>
                      </button>
                    );
                  })}
                </div>

                <SubLabel icon={Clock}>{t('recap_schedule.time_label', 'Time')}</SubLabel>
                <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto pr-1">
                  {times.map((tm) => {
                    const selected = recapTime === tm;
                    return (
                      <button
                        key={tm}
                        onClick={() => { setRecapTime(tm); updateSetting('recap_time', tm); }}
                        className="py-2 rounded-xl text-xs font-medium transition-all text-center"
                        style={glassOption(selected)}
                        data-testid={`settings-recap-time-${tm}`}
                      >
                        <span style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}>{formatTime(tm)}</span>
                      </button>
                    );
                  })}
                </div>

                <SubLabel icon={Globe}>{t('recap_schedule.timezone_label', 'Timezone')}</SubLabel>
                <select
                  value={recapTimezone}
                  onChange={(e) => { setRecapTimezone(e.target.value); updateSetting('recap_timezone', e.target.value); }}
                  className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                  style={{ background: 'var(--glass-bg)', border: '1.5px solid var(--glass-border)', color: 'var(--theme-text)', focusRingColor: 'var(--theme-accent)' }}
                  data-testid="settings-recap-timezone"
                >
                  <option value="">{t('recap_schedule.select_timezone', 'Select timezone...')}</option>
                  {timezones.map((z) => (<option key={z.value} value={z.value}>{z.label}</option>))}
                  {recapTimezone && !timezones.find(z => z.value === recapTimezone) && (
                    <option value={recapTimezone}>{recapTimezone}</option>
                  )}
                </select>

                {/* Push Notification Toggle */}
                <div
                  className="flex items-center justify-between mt-5 rounded-2xl px-4 py-3.5"
                  style={{
                    background: pushRecapEnabled ? 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.10))' : 'var(--glass-bg)',
                    border: `1.5px solid ${pushRecapEnabled ? 'rgba(99,102,241,0.3)' : 'var(--glass-border)'}`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: pushRecapEnabled ? 'var(--theme-accent)' : 'var(--glass-bg-active)' }}>
                      <Bell size={16} style={{ color: pushRecapEnabled ? '#fff' : 'var(--theme-textSecondary)' }} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold block" style={{ color: 'var(--theme-text)' }}>{t('settings.push_notifications', 'Push Notifications')}</span>
                      <span className="text-xs block mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.push_description', 'Get notified when your recap is ready')}</span>
                    </div>
                  </div>
                  <button
                    data-testid="settings-push-recap-toggle"
                    onClick={() => {
                      const newVal = !pushRecapEnabled;
                      setPushRecapEnabled(newVal);
                      updateSetting('push_recap_enabled', newVal);
                      if (newVal) {
                        // Subscribe to BOTH channels — OneSignal Web SDK
                        // (preferred) and VAPID (fallback). Either is fine.
                        requestOneSignalPermission().catch(() => {});
                        subscribeToPush().catch(() => {});
                      }
                    }}
                    className="relative w-12 h-7 rounded-full transition-all duration-200 shrink-0 ml-3"
                    style={{
                      background: pushRecapEnabled ? 'var(--theme-accent)' : 'var(--glass-bg-active)',
                      boxShadow: pushRecapEnabled ? '0 0 8px rgba(99,102,241,0.4)' : 'inset 0 1px 3px rgba(0,0,0,0.15)',
                      border: `1.5px solid ${pushRecapEnabled ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                    }}
                  >
                    <span
                      className="absolute top-[2px] left-[2px] w-[22px] h-[22px] rounded-full transition-transform duration-200"
                      style={{ transform: pushRecapEnabled ? 'translateX(20px)' : 'translateX(0)', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                    />
                  </button>
                </div>
              </div>

              {/* Daily Reflection Notifications */}
              <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-reflection-notif-section">
                <div
                  className="flex items-center justify-between rounded-2xl px-4 py-3.5"
                  style={{
                    background: pushReflectionEnabled ? 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.10))' : 'var(--glass-bg)',
                    border: `1.5px solid ${pushReflectionEnabled ? 'rgba(99,102,241,0.3)' : 'var(--glass-border)'}`,
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: pushReflectionEnabled ? 'var(--theme-accent)' : 'var(--glass-bg-active)' }}>
                      <BookOpen size={16} style={{ color: pushReflectionEnabled ? '#fff' : 'var(--theme-textSecondary)' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.preferencestab_daily_reflection_b86f')}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.preferencestab_get_notified_when_27c7')}</p>
                    </div>
                  </div>
                  <button
                    data-testid="settings-push-reflection-toggle"
                    onClick={() => {
                      const newVal = !pushReflectionEnabled;
                      setPushReflectionEnabled(newVal);
                      updateSetting('push_reflection_enabled', newVal);
                      if (newVal) {
                        requestOneSignalPermission().catch(() => {});
                        subscribeToPush().catch(() => {});
                      }
                    }}
                    className="relative w-12 h-7 rounded-full transition-all duration-200 shrink-0 ml-3"
                    style={{
                      background: pushReflectionEnabled ? 'var(--theme-accent)' : 'var(--glass-bg-active)',
                      boxShadow: pushReflectionEnabled ? '0 0 8px rgba(99,102,241,0.4)' : 'inset 0 1px 3px rgba(0,0,0,0.15)',
                      border: `1.5px solid ${pushReflectionEnabled ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                    }}
                  >
                    <span
                      className="absolute top-[2px] left-[2px] w-[22px] h-[22px] rounded-full transition-transform duration-200"
                      style={{ transform: pushReflectionEnabled ? 'translateX(20px)' : 'translateX(0)', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
